<template>
    <form @submit.prevent>
        <div class="error-row" v-if="errors.length > 0">
            <div v-for="error in errors">{{ error }}</div>
        </div>
        <div class="buy-now-row">
            <div class="price-container">
                <span class="price-header">
                    {{
                        labels.frontendProductpage.auction[
                            product.hasLoserOffer
                                ? "buyNowPrice_loserOffer"
                                : "buyNowPrice"
                        ]
                    }}:
                </span>
                <span class="price-value">{{
                    formatAmounts(product.buyPrice)
                }}</span>
            </div>
            <div class="buy-field-container">
                <cool-input :number-arrows="false">
                    <input
                        type="number"
                        @focus="checkLoggedIn($event)"
                        @input="checkLoggedIn($event)"
                        v-model.number="quantity"
                        min="1"
                        step="1"
                        :disabled="disabled"
                        @keypress="
                            checkLoggedIn($event) && disableDecimalKeys($event)
                        "
                        class="quantity-field"
                    />
                </cool-input>
            </div>
            <div class="button-column">
                <cool-button theme="turqoise" :busy="loading" :busy-size="26">
                    <button
                        type="button"
                        class="submit-buy"
                        :data-action="
                            revealActions.revealActions ? 'buy-now' : undefined
                        "
                        :disabled="disabled"
                        @click="checkLoggedIn($event) && submitBuy()"
                    >
                        {{
                            labels.frontendProductpage.auction[
                                product.hasLoserOffer
                                    ? "buyNowButton_loserOffer"
                                    : "buyNowButton"
                            ]
                        }}
                    </button>
                </cool-button>
            </div>
        </div>
    </form>
</template>

<style scoped lang="scss">
.error-row {
    color: rgb(151, 24, 24);
}

.buy-now-row {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;

    .buy-field-container {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: left;
        font-size: 20px;
        font-weight: 600;
        .quantity-field {
            font-size: 15px;
            padding: 5px;
            text-align: center;
            font-weight: 600;
            border: 2px solid #272727;
            border-radius: 4px;
            background-color: #ffffff;
            width: 3em;
        }
    }

    .button-column {
        margin-left: 10px;
    }

    .price-container {
        font-size: 18px;
        .price-header {
            font-weight: 700;
        }
        .price-value {
            font-weight: 800;
        }
    }
}

.submit-buy {
    border-radius: 300px;
    padding: 8px 20px;
    width: auto;
}
</style>

<script setup lang="ts">
import { type PropType, computed, ref } from "vue";
import CoolInput from "../../shared/CoolInput.vue";
import CoolButton from "../../shared/CoolButton.vue";
import { type AuctionProduct } from "./types";
import { createCurrencyFormatter } from "../common/amount-format";
import axios from "axios";
import { useRevealActions } from "../../../../state/reveal-actions";
const revealActions = useRevealActions();

const formatter = createCurrencyFormatter();
const props = defineProps({
    product: {
        type: Object as PropType<AuctionProduct>,
        required: true,
    },
});

const errors = ref<string[]>([]);
const quantity = ref<number>(1);
const loading = ref(false);
const disabled = computed(() => loading.value);

function formatAmounts(value: number) {
    return formatter.format(value);
}

function disableDecimalKeys(e: Event) {
    if (!(e instanceof KeyboardEvent)) {
        return;
    }
    if (
        //["Comma","Period", "Slash", "NumpadAdd","NumpadSubtract", "Minus", "KeyE"].includes(e.code)
        [",", ".", "+", "e"].includes(e.key)
    ) {
        e.preventDefault();
    }
}

import labels from "../../shared/labels";
import stringify from "qs/lib/stringify";
import { checkLoggedIn } from "./login";

async function submitBuy() {
    if (loading.value) return;
    loading.value = true;
    try {
        const value = quantity.value;
        if (typeof value !== "number" || isNaN(value)) {
            throw new Error("Invalid quantity");
        }

        const estimate = props.product.estimatedPrice;
        if (typeof estimate == "number" && estimate > 0 && value >= estimate) {
            const confirmLabel =
                labels.frontendProductpage.auction
                    .bidAboveEstimatedValueConfirmation;
            if (
                typeof confirmLabel === "string" &&
                confirmLabel.length > 0 &&
                !confirm(
                    labels.frontendProductpage.auction
                        .bidAboveEstimatedValueConfirmation
                )
            ) {
                return;
            }
        }
        const response = await axios.post<
            | {
                  Success: "True";
                  Message: string;
              }
            | {
                  Success: "Fail" | "False";
                  Message: string;
              }
        >(
            "/auctionProduct/BuyNow",
            stringify({
                productId: props.product.id,
                noOfItems: quantity.value,
            })
        );

        const data = response.data;
        if (data.Success !== "True") {
            if (data.Message != null) {
                console.error(data.Message);
                errors.value = [data.Message];
            } else {
                errors.value = ["Unknown error"];
            }
        } else {
            location.assign(data.Message);
        }
    } finally {
        loading.value = false;
    }
}
</script>
